import { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { formatCurrency } from "../../../utilities/formatCurrency";
import { DatabaseAdd, Pencil, Trash, ArrowLeft, ShieldExclamation } from "react-bootstrap-icons";
import {
  addOptionListItem,
  deleteListItem,
  editProductOption,
  fetchProductOptionList,
  updateOptionListItem,
} from "../../../lib/webService/webService";
import { useMerchantAuth, useMultiAuthCheck } from "../../../utilities/authCheck";
import { useUserContext } from "../../../context/UserContext";
import { toast } from "../../../utilities/toast";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";
import { useFormDraft } from "../../../hooks/useFormDraft";

if (typeof document !== "undefined" && !document.getElementById("eo-styles")) {
  const s = document.createElement("style");
  s.id = "eo-styles";
  s.textContent = `
    @keyframes _eo_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._eo_sk { animation: _eo_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .eo-input { background: rgba(118,118,128,0.10) !important; border: 1px solid transparent !important; border-radius: 10px !important; padding: 11px 14px !important; font-size: 0.9375rem !important; color: #1c1c1e !important; transition: border-color .18s, box-shadow .18s !important; width: 100%; outline: none; font-family: inherit; box-sizing: border-box; }
    .eo-input:focus { border-color: #007AFF !important; box-shadow: 0 0 0 3px rgba(0,122,255,0.12) !important; background: rgba(118,118,128,0.08) !important; }
    .eo-input::placeholder { color: rgba(60,60,67,0.32) !important; }
    .eo-input.eo-invalid { border-color: #FF3B30 !important; box-shadow: 0 0 0 3px rgba(255,59,48,0.10) !important; }
    .eo-modal .modal-content { border-radius: 16px !important; border: none !important; overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.18) !important; }
  `;
  document.head.appendChild(s);
}

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_eo_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 7 }}>
    {children}
  </label>
);

// ─── Shared modal footer buttons ──────────────────────────────────────────────

const ModalCancelBtn = ({ onClick }) => (
  <button onClick={onClick} style={{ padding: "9px 18px", borderRadius: 10, border: "1px solid rgba(60,60,67,.15)", background: "transparent", color: "rgba(60,60,67,.7)", fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", fontFamily: FONT }}>
    Cancel
  </button>
);

// ─── Custom hooks ─────────────────────────────────────────────────────────────

const useApiState = (initialState = {}) => {
  const [state, setState] = useState({ loading: false, error: null, data: null, ...initialState });
  const setLoading = useCallback((loading) => setState((p) => ({ ...p, loading, error: null })), []);
  const setError   = useCallback((error)   => setState((p) => ({ ...p, loading: false, error })), []);
  const setData    = useCallback((data)    => setState((p) => ({ ...p, loading: false, error: null, data })), []);
  return { state, setLoading, setError, setData };
};

const useFormState = (initialState = {}) => {
  const [formState, setFormState] = useState({ validated: false, loading: false, error: null, ...initialState });
  const setFormLoading = useCallback((loading) => setFormState((p) => ({ ...p, loading, error: null })), []);
  const setFormError   = useCallback((error)   => setFormState((p) => ({ ...p, loading: false, error })), []);
  const setValidated   = useCallback((v)        => setFormState((p) => ({ ...p, validated: v })), []);
  return { formState, setFormLoading, setFormError, setValidated };
};

// ─── Add Item Modal ───────────────────────────────────────────────────────────

const AddItemModal = ({ onAdd }) => {
  const { productId, optionId } = useParams();
  const { token } = useUserContext();
  const [show,   setShow]   = useState(false);
  const [value,  setValue]  = useState("");
  const [amount, setAmount] = useState(0);
  const { formState, setFormLoading, setFormError } = useFormState();

  const handleClose = () => {
    setShow(false);
    setValue("");
    setAmount(0);
    setFormError(null);
  };
  const handleShow = () => setShow(true);

  const handleValueChange  = (e) => { setValue(e.target.value); if (e.target.value) setFormError(null); };
  const handleAmountChange = (e) => { setAmount(Number(e.target.value)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) { setFormError({ message: "Value is required" }); return; }
    try {
      setFormLoading(true);
      const item = { value: value.trim(), amount: amount || 0 };
      await addOptionListItem([item], productId, optionId, token);
      toast.success("Item added successfully!");
      onAdd?.();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to add item. Try again later.");
      setFormLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleShow}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 20, border: "1px solid rgba(60,60,67,.18)", background: "transparent", color: "rgba(60,60,67,.72)", fontSize: "0.8125rem", fontWeight: 500, cursor: "pointer", fontFamily: FONT }}
      >
        <DatabaseAdd size={14} />
        Add items
      </button>
      <Modal show={show} onHide={handleClose} backdrop="static" centered dialogClassName="eo-modal">
        <Modal.Body style={{ padding: 0 }}>
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(60,60,67,.09)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: 0, letterSpacing: "-0.02em" }}>New Item</h3>
            <button onClick={handleClose} style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "1.1rem", color: "rgba(60,60,67,.55)", lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {formState.error && (
              <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: "0.875rem", color: "#FF3B30" }}>
                {formState.error.message}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <FieldLabel htmlFor="eo-add-value">Value</FieldLabel>
                <input
                  id="eo-add-value"
                  type="text"
                  className={`eo-input${formState.error ? " eo-invalid" : ""}`}
                  value={value}
                  onChange={handleValueChange}
                  placeholder="Enter value"
                  required
                />
              </div>
              <div>
                <FieldLabel htmlFor="eo-add-amount">Amount</FieldLabel>
                <input
                  id="eo-add-amount"
                  type="number"
                  className="eo-input"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0"
                  required
                />
              </div>
            </div>
          </div>
          <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <ModalCancelBtn onClick={handleClose} />
            <button
              onClick={handleSubmit}
              disabled={formState.loading}
              style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: formState.loading ? "rgba(0,122,255,.5)" : "#007AFF", color: "#fff", fontSize: "0.875rem", fontWeight: 600, cursor: formState.loading ? "not-allowed" : "pointer", fontFamily: FONT }}
            >
              {formState.loading ? "Saving…" : "Save Item"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

const DeleteModal = ({ id, onDelete }) => {
  const { productId, optionId } = useParams();
  const { token } = useUserContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { formState, setFormLoading, setFormError } = useFormState();

  const handleShowDeleteModal = () => setShowDeleteModal(true);
  const handleHideDeleteModal = () => { setShowDeleteModal(false); setFormError(null); };

  const handleDelete = async () => {
    try {
      setFormLoading(true);
      await deleteListItem(productId, optionId, id, token);
      toast.success("Item deleted successfully!");
      onDelete?.();
      handleHideDeleteModal();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to delete item. Try again later.");
      setFormLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleShowDeleteModal}
        style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,59,48,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#FF3B30", transition: "background .15s" }}
      >
        <Trash size={14} />
      </button>
      <Modal show={showDeleteModal} onHide={handleHideDeleteModal} centered dialogClassName="eo-modal">
        <Modal.Body style={{ padding: 0 }}>
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(60,60,67,.09)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: 0, letterSpacing: "-0.02em" }}>Delete Item</h3>
            <button onClick={handleHideDeleteModal} style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "1.1rem", color: "rgba(60,60,67,.55)", lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            <p style={{ fontSize: "0.9375rem", color: "rgba(60,60,67,.65)", lineHeight: 1.5, margin: 0 }}>
              Are you sure you want to delete this item? This action cannot be undone.
            </p>
          </div>
          <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <ModalCancelBtn onClick={handleHideDeleteModal} />
            <button
              onClick={handleDelete}
              disabled={formState.loading}
              style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: formState.loading ? "rgba(255,59,48,.5)" : "#FF3B30", color: "#fff", fontSize: "0.875rem", fontWeight: 600, cursor: formState.loading ? "not-allowed" : "pointer", fontFamily: FONT }}
            >
              {formState.loading ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────

const EditModal = ({ id, v, a, onEdit }) => {
  const { productId, optionId } = useParams();
  const { token } = useUserContext();
  const [value,  setValue]  = useState(v);
  const [amount, setAmount] = useState(a);
  const [showEditModal, setShowEditModal] = useState(false);
  const { formState, setFormLoading, setFormError } = useFormState();

  const handleShowEditModal = () => setShowEditModal(true);
  const handleHideEditModal = () => { setShowEditModal(false); setValue(v); setAmount(a); setFormError(null); };

  const handleValueChange  = (e) => { setValue(e.target.value); if (e.target.value) setFormError(null); };
  const handleAmountChange = (e) => { setAmount(Number(e.target.value)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) { setFormError({ message: "Value is required" }); return; }
    try {
      setFormLoading(true);
      const data = { id, value: value.trim(), amount: amount || 0 };
      await updateOptionListItem(productId, optionId, id, data, token);
      toast.success("Item updated successfully!");
      onEdit?.();
      handleHideEditModal();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to update item. Try again later.");
      setFormLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleShowEditModal}
        style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(0,122,255,.09)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#007AFF", transition: "background .15s" }}
      >
        <Pencil size={14} />
      </button>
      <Modal show={showEditModal} onHide={handleHideEditModal} backdrop="static" centered dialogClassName="eo-modal">
        <Modal.Body style={{ padding: 0 }}>
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(60,60,67,.09)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: 0, letterSpacing: "-0.02em" }}>Edit Item</h3>
            <button onClick={handleHideEditModal} style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "1.1rem", color: "rgba(60,60,67,.55)", lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {formState.error && (
              <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: "0.875rem", color: "#FF3B30" }}>
                {formState.error.message}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <FieldLabel htmlFor="eo-edit-value">Value</FieldLabel>
                <input
                  id="eo-edit-value"
                  type="text"
                  className={`eo-input${formState.error ? " eo-invalid" : ""}`}
                  value={value}
                  onChange={handleValueChange}
                  placeholder="Enter value"
                  required
                />
              </div>
              <div>
                <FieldLabel htmlFor="eo-edit-amount">Amount</FieldLabel>
                <input
                  id="eo-edit-amount"
                  type="number"
                  className="eo-input"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0"
                  required
                />
              </div>
            </div>
          </div>
          <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <ModalCancelBtn onClick={handleHideEditModal} />
            <button
              onClick={handleSubmit}
              disabled={formState.loading}
              style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: formState.loading ? "rgba(0,122,255,.5)" : "#007AFF", color: "#fff", fontSize: "0.875rem", fontWeight: 600, cursor: formState.loading ? "not-allowed" : "pointer", fontFamily: FONT }}
            >
              {formState.loading ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const EditOption = () => {
  const { productId, optionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useUserContext();
  const { isAuthorized, isLoading: authLoading, logout } = useMultiAuthCheck();

  const [name,     setName]     = useState("");
  const [title,    setTitle]    = useState("");
  const [required, setRequired] = useState(false);
  const [itemsArr, setItemsArr] = useState([]);

  const { formState, setFormLoading, setFormError, setValidated } = useFormState();
  const { state: apiState, setLoading, setError, setData } = useApiState();

  const { restoredDraft, clearDraft } = useFormDraft(`edit-option:${optionId}`, {
    name, title, required,
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        const source = restoredDraft ?? {
          name: location.state?.name,
          title: location.state?.title,
          required: location.state?.required,
        };
        setName(source.name ?? "");
        setTitle(source.title ?? "");
        setRequired(source.required ?? false);
        const list = await fetchProductOptionList(productId, optionId);
        console.log("Fetched option items:", list);
        setData(list);
        setItemsArr(list);
      } catch (error) {
        setError({ message: "Failed to load option data. Please try again." });
      }
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, optionId]);

  const handleTitleChange  = (e) => { setTitle(e.target.value); };
  const handleNameChange   = (e) => { setName(e.target.value); };
  const handleRadioChange  = (e) => { setRequired(e.target.value === "true"); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setValidated(true);
    if (!name.trim() || !title.trim()) {
      setFormError({ message: "Please fill in all required fields" });
      return;
    }
    try {
      const op = { id: optionId, title: title.trim(), name: name.trim(), required };
      await editProductOption(op, productId, optionId, token);
      toast.success("Option updated successfully!");
      clearDraft();
      navigate(-1);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to update option. Try again later.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleItemUpdate = useCallback(() => {
    fetchProductOptionList(productId, optionId)
      .then((list) => { setItemsArr(list); })
      .catch((error) => { setError({ message: "Failed to refresh items. Please try again." }); });
  }, [productId, optionId]);

  if (authLoading) {
    return (
      <div style={PAGE}>
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} /><Skel w={120} h={22} r={7} />
          </div>
          <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.08)", padding: 20 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <Skel w={60} h={11} r={5} style={{ marginBottom: 8 }} />
                <Skel w="100%" h={42} r={10} />
              </div>
            ))}
            <Skel w="100%" h={44} r={12} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: 32 }}>
          <div style={{ width: 60, height: 60, borderRadius: 18, backgroundColor: "rgba(255,149,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>Access Restricted</h3>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,.5)", textAlign: "center", marginBottom: 24 }}>Merchant authentication required.</p>
          <button onClick={logout} style={{ padding: "11px 28px", borderRadius: 12, border: "none", backgroundColor: "#007AFF", color: "#fff", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
            Sign In as Merchant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Edit Option")} />
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Back nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1c1c1e", flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>Edit Option</h2>
        </div>

        <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.09)", padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
          <form noValidate onSubmit={handleSubmit}>

            {apiState.error && (
              <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 10, padding: "11px 14px", marginBottom: 20, fontSize: "0.875rem", color: "#FF3B30" }}>
                {apiState.error.message}
              </div>
            )}

            {/* Title + Name */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <FieldLabel htmlFor="eo-title">Title</FieldLabel>
                <input
                  id="eo-title"
                  type="text"
                  className={`eo-input${formState.validated && !title.trim() ? " eo-invalid" : ""}`}
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter title"
                  required
                />
                {formState.validated && !title.trim() && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a title</div>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="eo-name">Name</FieldLabel>
                <input
                  id="eo-name"
                  type="text"
                  className={`eo-input${formState.validated && !name.trim() ? " eo-invalid" : ""}`}
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Enter name"
                  required
                />
                {formState.validated && !name.trim() && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a name</div>
                )}
              </div>
            </div>

            {/* Required toggle */}
            <div style={{ marginBottom: 20 }}>
              <FieldLabel>Required?</FieldLabel>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => handleRadioChange({ target: { value: "true" } })}
                  style={{
                    padding: "8px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                    fontSize: "0.875rem", fontWeight: 600, fontFamily: FONT,
                    backgroundColor: required ? "#007AFF" : "rgba(118,118,128,0.10)",
                    color: required ? "#fff" : "rgba(60,60,67,0.7)",
                    transition: "background-color .15s, color .15s",
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => handleRadioChange({ target: { value: "false" } })}
                  style={{
                    padding: "8px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                    fontSize: "0.875rem", fontWeight: 600, fontFamily: FONT,
                    backgroundColor: !required ? "#007AFF" : "rgba(118,118,128,0.10)",
                    color: !required ? "#fff" : "rgba(60,60,67,0.7)",
                    transition: "background-color .15s, color .15s",
                  }}
                >
                  No
                </button>
              </div>
            </div>

            {/* Items section */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e", letterSpacing: "-0.015em" }}>Option Items</div>
              <AddItemModal onAdd={handleItemUpdate} />
            </div>

            {apiState.loading ? (
              <div style={{ marginBottom: 20 }}>
                {[1, 2, 3].map((i) => (
                  <div key={i} style={{ backgroundColor: "#F2F2F7", borderRadius: 12, padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <Skel w={120} h={15} r={5} style={{ marginBottom: 6 }} />
                      <Skel w={80} h={12} r={4} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Skel w={32} h={32} r={16} />
                      <Skel w={32} h={32} r={16} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                {itemsArr.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      backgroundColor: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 8,
                      border: "1px solid rgba(60,60,67,.09)",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 2 }}>{item.value}</div>
                      <div style={{ fontSize: "0.8125rem", color: "#34C759", fontWeight: 500 }}>{formatCurrency(item.amount)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <EditModal  id={item.id} v={item.value} a={item.amount} onEdit={handleItemUpdate} />
                      <DeleteModal id={item.id} onDelete={handleItemUpdate} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formState.error && (
              <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 10, padding: "11px 14px", marginBottom: 16, fontSize: "0.875rem", color: "#FF3B30" }}>
                {formState.error.message}
              </div>
            )}

            <button
              type="submit"
              disabled={formState.loading}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, border: "none",
                backgroundColor: formState.loading ? "rgba(0,122,255,.5)" : "#007AFF",
                color: "#fff", fontSize: "0.9375rem", fontWeight: 600,
                cursor: formState.loading ? "not-allowed" : "pointer",
                fontFamily: FONT, transition: "background-color .18s",
              }}
            >
              {formState.loading ? "Saving…" : "Save Changes"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default EditOption;
